import {
  CURRENT_ACCESS,
  EMPTY_FILTERS,
  readUrlState,
  writeUrlState,
} from './urlState';

const at = (search) =>
  window.history.replaceState({}, '', `/management/users${search}`);

beforeEach(() => at(''));

describe('management/Users urlState', () => {
  describe('readUrlState', () => {
    it('defaults everything when the URL is bare', () => {
      expect(readUrlState()).toEqual({
        access: CURRENT_ACCESS,
        filters: EMPTY_FILTERS,
        page: 1,
        query: '',
        sortBy: null,
        sortOrder: null,
      });
    });

    it('reads a page number', () => {
      at('?page=4');

      expect(readUrlState().page).toEqual(4);
    });

    // A hand-edited or stale URL must not put the table into a broken state.
    it.each([
      ['non-numeric', '?page=abc'],
      ['negative', '?page=-1'],
      ['zero', '?page=0'],
      ['empty', '?page='],
      ['fractional', '?page=1.5x'],
    ])('falls back to page 1 for a %s page', (_label, search) => {
      at(search);

      expect(readUrlState().page).toEqual(1);
    });

    it('reads the sort field and direction', () => {
      at('?sort_by=job_role&sort_order=DESC');

      expect(readUrlState()).toMatchObject({
        sortBy: 'job_role',
        sortOrder: 'DESC',
      });
    });

    it('defaults the direction to ASC when only the field is given', () => {
      at('?sort_by=job_role');

      expect(readUrlState().sortOrder).toEqual('ASC');
    });

    it('falls back to ASC for an unrecognised direction', () => {
      at('?sort_by=job_role&sort_order=sideways');

      expect(readUrlState().sortOrder).toEqual('ASC');
    });

    // Without a field there is nothing to order, so the direction is dropped
    // rather than left dangling.
    it('reports no direction when there is no field', () => {
      at('?sort_order=DESC');

      expect(readUrlState()).toMatchObject({ sortBy: null, sortOrder: null });
    });

    it('reads the search term', () => {
      at('?q=martinez');

      expect(readUrlState().query).toEqual('martinez');
    });

    it('reads repeated filter params into arrays', () => {
      at('?account_ids[]=1&account_ids[]=2&permissions[]=Member');

      expect(readUrlState().filters).toEqual({
        accountIds: ['1', '2'],
        permissions: ['Member'],
        jobRoles: [],
      });
    });
  });

  describe('writeUrlState', () => {
    const write = (overrides = {}) =>
      writeUrlState({
        filters: EMPTY_FILTERS,
        page: 1,
        query: '',
        sortBy: null,
        sortOrder: null,
        ...overrides,
      });

    it('leaves the URL bare for the default view', () => {
      write();

      expect(window.location.search).toEqual('');
    });

    it('omits the page on the first page', () => {
      write({ page: 1 });

      expect(window.location.search).not.toContain('page');
    });

    it('records a later page', () => {
      write({ page: 3 });

      expect(window.location.search).toEqual('?page=3');
    });

    it('omits the sort when no field is set', () => {
      write({ sortOrder: 'DESC' });

      expect(window.location.search).toEqual('');
    });

    it('records the sort field and direction', () => {
      write({ sortBy: 'job_role', sortOrder: 'DESC' });

      expect(window.location.search).toEqual(
        '?sort_by=job_role&sort_order=DESC',
      );
    });

    it('defaults the direction when a field has none', () => {
      write({ sortBy: 'job_role' });

      expect(window.location.search).toContain('sort_order=ASC');
    });

    it('records the search term', () => {
      write({ query: 'martinez' });

      expect(window.location.search).toEqual('?q=martinez');
    });

    it('repeats a filter param per value', () => {
      write({ filters: { ...EMPTY_FILTERS, accountIds: ['1', '2'] } });

      expect(decodeURIComponent(window.location.search)).toEqual(
        '?account_ids[]=1&account_ids[]=2',
      );
    });

    it('survives filters missing a key entirely', () => {
      expect(() => write({ filters: {} })).not.toThrow();
    });

    // Rewriting on every page and filter change would otherwise drop it.
    it('keeps any hash the user arrived with', () => {
      at('#section');

      write({ page: 2 });

      expect(window.location.hash).toEqual('#section');
    });

    // Paging is not navigation history — Back should leave the page, not step
    // through every page the user viewed.
    it('replaces the history entry rather than pushing one', () => {
      const replaceState = jest.spyOn(window.history, 'replaceState');
      const pushState = jest.spyOn(window.history, 'pushState');

      write({ page: 2 });

      expect(replaceState).toHaveBeenCalled();
      expect(pushState).not.toHaveBeenCalled();

      replaceState.mockRestore();
      pushState.mockRestore();
    });

    it('round-trips through readUrlState', () => {
      write({
        filters: { ...EMPTY_FILTERS, jobRoles: ['technician'] },
        page: 2,
        query: 'abe',
        sortBy: 'job_role',
        sortOrder: 'DESC',
      });

      expect(readUrlState()).toEqual({
        access: CURRENT_ACCESS,
        filters: { ...EMPTY_FILTERS, jobRoles: ['technician'] },
        page: 2,
        query: 'abe',
        sortBy: 'job_role',
        sortOrder: 'DESC',
      });
    });
  });
});
